import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './CategoryDrawer.css';

interface CategoryDrawerProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    learnMoreUrl?: string;
    children: React.ReactNode;
}

const CategoryDrawer: React.FC<CategoryDrawerProps> = ({ open, onClose, title, description, learnMoreUrl, children }) => {
    const { t } = useTranslation('knowledgeGraph');

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
    }, [onClose]);

    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [open, handleKeyDown]);

    if (!open) return null;

    return (
        <div className="kg-modal-overlay" onClick={onClose}>
            <div
                className="kg-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="kg-modal-title"
            >
                <div className="kg-modal__header">
                    <h3 id="kg-modal-title" className="kg-modal__title">{title}</h3>
                    <button className="kg-modal__close" onClick={onClose} aria-label="Close">
                        &times;
                    </button>
                </div>
                <div className="kg-modal__body">
                    {description && (
                        <p className="kg-modal__desc">{description}</p>
                    )}
                    {children}
                </div>
                {learnMoreUrl && (
                    <div className="kg-modal__footer">
                        <a
                            href={learnMoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="kg-modal__learn-more"
                        >
                            {t('drawer.learnMore', 'Learn More')}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                <polyline points="15 3 21 3 21 9"/>
                                <line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CategoryDrawer;
